import type { PlayerProfileResponse } from '../../../../../services/player/playerProfile'
import { PLAYER_DETAILS_FIELD_SECTIONS } from '../constants'
import type {
  PlayerDetailsAdminField,
  PlayerDetailsAdminValues,
  PlayerDetailsFieldDef,
  PlayerDetailsFieldSectionKey,
} from '../types'
import { mapAdminFieldsFromPlayer } from './mapPlayerDetailsToForm'
import { isAdminField } from './sectionFields'
import { toAdminPayload } from './toAdminPayload'

/**
 * The Player Details sections the Edit Player forms (profile and roster) show
 * too, in this order. Adding a key here adds the section's fields to the
 * forms' reads and saves; each platform renders it from the same definition.
 */
export const PROFILE_EXTRA_SECTIONS: ReadonlyArray<PlayerDetailsFieldSectionKey> =
  ['experience', 'preferences', 'additional', 'notes']

/**
 * Who is editing: the player on their own profile (`self`), or league staff on
 * a roster (`admin`). `self` leaves out the fields marked `adminOnly`.
 */
export type ProfileExtrasAudience = 'self' | 'admin'

interface ProfileExtrasOptions {
  sections?: ReadonlyArray<PlayerDetailsFieldSectionKey>
  audience?: ProfileExtrasAudience
}

/** Whether this audience gets the section at all (`self` skips admin-only ones). */
export const isProfileExtraSectionShown = (
  sectionKey: PlayerDetailsFieldSectionKey,
  audience: ProfileExtrasAudience = 'admin'
): boolean =>
  audience === 'admin' || !PLAYER_DETAILS_FIELD_SECTIONS[sectionKey].adminOnly

/** A section's field definitions as this audience sees them. */
export const profileExtraSectionFields = (
  sectionKey: PlayerDetailsFieldSectionKey,
  audience: ProfileExtrasAudience = 'admin'
): ReadonlyArray<PlayerDetailsFieldDef> =>
  isProfileExtraSectionShown(sectionKey, audience)
    ? PLAYER_DETAILS_FIELD_SECTIONS[sectionKey].fields.filter(
        (def) =>
          !def.playerDetailsOnly && (audience === 'admin' || !def.adminOnly)
      )
    : []

/**
 * Whether the section has anything to edit for this audience. One that only
 * shows registration-derived values (Notes → Registration Notes) is display
 * only — and has nothing to show when adding a new player.
 */
export const profileExtraSectionHasEditableFields = (
  sectionKey: PlayerDetailsFieldSectionKey,
  audience: ProfileExtrasAudience = 'admin'
): boolean =>
  profileExtraSectionFields(sectionKey, audience).some((def) => !def.locked)

/**
 * The admin fields those sections hold for this audience, read-only ones
 * included. Fields the Edit Player form already owns (e.g. Preferred Language)
 * are left to its own mapping and payload.
 */
export const profileExtraFields = ({
  sections = PROFILE_EXTRA_SECTIONS,
  audience = 'admin',
}: ProfileExtrasOptions = {}): PlayerDetailsAdminField[] =>
  sections.flatMap((key) =>
    profileExtraSectionFields(key, audience)
      .map((def) => def.name)
      .filter(isAdminField)
  )

/** A player response → the extra sections' form values. */
export const mapProfileExtrasToForm = (
  player: PlayerProfileResponse,
  options?: ProfileExtrasOptions
): Partial<PlayerDetailsAdminValues> =>
  mapAdminFieldsFromPlayer(player, profileExtraFields(options))

/**
 * The extra sections' changed fields, keyed by API name, to merge into the
 * form's PUT body. Read-only fields (e.g. Previous Years Played) are never sent.
 */
export const toProfileExtrasPayload = (
  current: PlayerDetailsAdminValues,
  initial: PlayerDetailsAdminValues,
  options?: ProfileExtrasOptions
): Record<string, string | boolean> =>
  toAdminPayload(current, initial, profileExtraFields(options))
